// =============================================================================
// Origin UI — src/pages/employees/TeamViewPage.tsx
// Org hierarchy view. Shows the current user's manager, themselves, and
// their direct reports. HR Admin can browse anyone's tree by clicking.
// Phase 4 — Employee Records module.
// Updated: Resigned / Terminated / Departed staff excluded from the org chart
//          (they remain visible in the Employees list status tabs).
// Oct 2026: Org chart PDF download (A4 landscape) for all users — whole
//          organisation, a single department, or the team currently shown.
// =============================================================================

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/services/apiService';
import { useAuth } from '@/hooks/useAuth';
import { PageHeader, Card, ErrorBanner, LoadingSpinner } from '@/components/common';
import { generateOrgChartPdf, type OrgChartPerson, type OrgChartScope } from '@/services/orgChartPdfGenerator';
import styles from './employees.module.css';

interface EmployeeListItem {
  employeeId: string;
  employeeNumber: string;
  firstName: string;
  lastName: string;
  preferredName: string | null;
  preferredFirstName: string | null;
  preferredLastName: string | null;
  displayName: string | null;
  email: string;
  phone: string | null;
  profilePhotoUrl: string | null;
  jobTitle: string;
  departmentName: string;
  officeName: string;
  employmentType: string;
  employmentStatus: string;
  joinDate: string;
  managerId: string | null;
  managerName: string | null;
}

// Statuses that mean the person has left — never shown on the org chart.
// Mirrors _inactiveStatuses in LeaveService.cs, plus 'Departed' (soft delete).
const INACTIVE_STATUSES = new Set(['Resigned', 'Terminated', 'Departed']);

export function TeamViewPage() {
  const [employees, setEmployees] = useState<EmployeeListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [focusId, setFocusId] = useState<string | null>(null);
  const [myEmployeeId, setMyEmployeeId] = useState<string | null>(null);
  const [exportScope, setExportScope] = useState<string>('all');
  const [exporting, setExporting] = useState(false);
  const { roles } = useAuth();
  const navigate = useNavigate();

  useEffect(() => { loadEmployees(); }, []);

  async function loadEmployees() {
    try {
      setLoading(true);
      const all = await api.get<EmployeeListItem[]>('/api/employees');
      const data = all.filter(e => !INACTIVE_STATUSES.has(e.employmentStatus));
      setEmployees(data);

      // Find current user's employee record (the one with managerId relationships)
      // For HR Admin, default to showing the CEO (no manager)
      const me = await api.get<{ employeeId: string }>('/api/employees/me').catch(() => null);
      if (me) {
        setFocusId(me.employeeId);
        setMyEmployeeId(me.employeeId);
      } else if (data.length > 0) {
        // Fallback: show the person with no manager (CEO)
        const ceo = data.find(e => !e.managerId);
        setFocusId(ceo?.employeeId || data[0].employeeId);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load team data');
    } finally {
      setLoading(false);
    }
  }

  if (loading) return <LoadingSpinner message="Loading team view..." />;
  if (error) return <ErrorBanner message={error} />;

  const focused = employees.find(e => e.employeeId === focusId);
  const manager = focused?.managerId ? employees.find(e => e.employeeId === focused.managerId) : null;
  const today = new Date().toISOString().split('T')[0];
  const directReports = employees.filter(e => e.managerId === focusId && e.joinDate.split('T')[0] <= today);

  function getInitials(first: string, last: string): string {
    return ((first?.[0] || '') + (last?.[0] || '')).toUpperCase();
  }

  function getDisplayName(e: EmployeeListItem): string {
    if (e.displayName) return e.displayName;
    const first = e.preferredFirstName || e.preferredName || e.firstName;
    const last = e.preferredLastName || e.lastName;
    return first + " " + last;
  }

  function PersonCard({ emp, label, size = 'normal' }: { emp: EmployeeListItem; label?: string; size?: 'normal' | 'large' }) {
    const isLarge = size === 'large';
    // Who can click through to a profile:
    // - HR Admin and OQTMan: any card (OQTMan is read-only)
    // - Everyone else: own card only (managers have no access to reports' profiles)
    const isSelf = emp.employeeId === myEmployeeId;
    const canClick = roles.isHRAdmin || roles.isOQTMan || isSelf;
    return (
      <div
        className={`${styles.teamCard} ${isLarge ? styles.teamCardLarge : ''}`}
        onClick={canClick ? () => navigate(`/employees/${emp.employeeId}`) : undefined}
        style={{ cursor: canClick ? 'pointer' : 'default' }}
      >
        <div className={isLarge ? styles.teamAvatarLarge : styles.teamAvatar}>
          {getInitials(emp.firstName, emp.lastName)}
        </div>
        <div className={styles.teamCardInfo}>
          {label && <div className={styles.teamCardLabel}>{label}</div>}
          <div className={isLarge ? styles.teamCardNameLarge : styles.teamCardName}>{getDisplayName(emp)}</div>
          <div className={styles.teamCardRole}>{emp.jobTitle}</div>
          <div className={styles.teamCardMeta}>{emp.departmentName} · {emp.officeName}</div>
        </div>
        {focusId !== emp.employeeId && (
          <button
            className={styles.teamFocusBtn}
            onClick={(e) => { e.stopPropagation(); setFocusId(emp.employeeId); }}
            title="View their team"
          >
            👥
          </button>
        )}
      </div>
    );
  }

  const ceoId = employees.find(e => !e.managerId)?.employeeId || null;

  // ─── Org chart PDF ───
  // Current staff only: pre-starters (join date in the future) are left off,
  // matching what Team View shows.
  const departments = Array.from(new Set(employees.map(e => e.departmentName).filter(Boolean))).sort();

  function handleDownloadOrgChart() {
    setExporting(true);
    try {
      const people: OrgChartPerson[] = employees
        .filter(e => e.joinDate.split('T')[0] <= today)
        .map(e => ({
          employeeId: e.employeeId,
          name: getDisplayName(e),
          jobTitle: e.jobTitle,
          departmentName: e.departmentName,
          officeName: e.officeName,
          managerId: e.managerId,
        }));

      let scope: OrgChartScope = { kind: 'all' };
      if (exportScope === 'team' && focusId) scope = { kind: 'team', rootId: focusId };
      else if (exportScope.startsWith('dept:')) scope = { kind: 'department', department: exportScope.slice(5) };

      generateOrgChartPdf(people, scope);
    } catch (err: any) {
      setError(err.message || 'Failed to generate org chart PDF');
    } finally {
      setExporting(false);
    }
  }

  const exportControls = (
    <div className={styles.orgExportBar}>
      <select
        className={styles.orgExportSelect}
        value={exportScope}
        onChange={e => setExportScope(e.target.value)}
        aria-label="Org chart scope"
      >
        <option value="all">Entire organisation</option>
        {focused && <option value="team">{getDisplayName(focused)}'s team</option>}
        <optgroup label="Department">
          {departments.map(d => (
            <option key={d} value={`dept:${d}`}>{d}</option>
          ))}
        </optgroup>
      </select>
      <button className={styles.orgExportBtn} onClick={handleDownloadOrgChart} disabled={exporting}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M12 3v12" /><path d="M7 10l5 5 5-5" /><path d="M5 21h14" />
        </svg>
        {exporting ? 'Preparing…' : 'Org Chart PDF'}
      </button>
    </div>
  );

  return (
    <div>
      <PageHeader
        title="Team View"
        subtitle={focused ? `Showing team for ${getDisplayName(focused)}` : 'Organisation hierarchy'}
        action={exportControls}
      />

      {/* Navigation: show reset buttons when browsing away from own record */}
      {focused && focusId !== myEmployeeId && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          {myEmployeeId && focusId !== myEmployeeId && (
            <button className={styles.addSmallBtn} style={{ background: 'var(--navy)', color: 'white', border: 'none' }} onClick={() => setFocusId(myEmployeeId)}>
              ← My Team
            </button>
          )}
          {ceoId && focusId !== ceoId && (
            <button className={styles.addSmallBtn} style={{ background: 'var(--gray-200, #e2e2e2)', color: 'var(--navy)', border: 'none' }} onClick={() => setFocusId(ceoId)}>
              ⬆ Top of Org
            </button>
          )}
        </div>
      )}

      {/* Manager */}
      {manager && (
        <div className={styles.teamSection}>
          <div className={styles.teamSectionLabel}>Reports to</div>
          <div className={styles.teamConnector} />
          <PersonCard emp={manager} label="Manager" />
        </div>
      )}

      {/* Focused person */}
      {focused && (
        <div className={styles.teamSection}>
          {manager && <div className={styles.teamConnector} />}
          <PersonCard emp={focused} size="large" />
        </div>
      )}

      {/* Direct reports */}
      {directReports.length > 0 && (
        <div className={styles.teamSection}>
          <div className={styles.teamConnector} />
          <div className={styles.teamSectionLabel}>Direct reports ({directReports.length})</div>
          <div className={styles.teamGrid}>
            {directReports.map(emp => (
              <PersonCard key={emp.employeeId} emp={emp} />
            ))}
          </div>
        </div>
      )}

      {directReports.length === 0 && focused && (
        <div className={styles.teamSection}>
          <Card>
            <p style={{ textAlign: 'center', color: 'var(--gray-500)', padding: 16, fontSize: 14 }}>
              No direct reports.
            </p>
          </Card>
        </div>
      )}
    </div>
  );
}
